// Discreet feedback strip under each AI answer: 👍 👎 + "Report wrong answer".
// 👍 submits immediately; 👎/wrong opens a small INLINE comment box (optional)
// before sending. Pressing another option later just changes the rating —
// api/feedback.js keeps one row per (user, answer).
import { useState } from 'react';
import { sendFeedback } from './feedbackApi';

const THANKS = 'Thanks — this helps improve answers.';

const smallBtn = active => ({
  background: active ? 'var(--surface-3, #f1f3f9)' : 'transparent',
  border: `1px solid ${active ? 'var(--border, #e3e7f0)' : 'transparent'}`,
  borderRadius: 8,
  padding: '3px 8px',
  fontSize: 13,
  cursor: 'pointer',
  lineHeight: 1,
});

export default function FeedbackRow({ queryLogId }) {
  const [rating, setRating] = useState(null); // last submitted or pending choice
  const [commentFor, setCommentFor] = useState(null); // 'down' | 'wrong' | null
  const [comment, setComment] = useState('');
  const [thanks, setThanks] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!queryLogId) return null;

  async function submit(nextRating, nextComment) {
    setBusy(true);
    setError(null);
    try {
      await sendFeedback({ queryLogId, rating: nextRating, comment: nextComment });
      setRating(nextRating);
      setThanks(true);
      setCommentFor(null);
      setComment('');
    } catch (e) {
      setError(e.message || 'Could not send feedback.');
    } finally {
      setBusy(false);
    }
  }

  function pick(next) {
    if (busy) return;
    if (next === 'up') {
      submit('up', '');
    } else {
      // Ask for an optional short comment before sending 👎 / wrong.
      setThanks(false);
      setCommentFor(next);
    }
  }

  return (
    <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--text-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <button
          type="button"
          aria-label="Good answer"
          title="Good answer"
          style={smallBtn(rating === 'up')}
          onClick={() => pick('up')}
          disabled={busy}
        >
          👍
        </button>
        <button
          type="button"
          aria-label="Bad answer"
          title="Bad answer"
          style={smallBtn(rating === 'down')}
          onClick={() => pick('down')}
          disabled={busy}
        >
          👎
        </button>
        <button
          type="button"
          style={{
            background: 'transparent',
            border: 'none',
            color: rating === 'wrong' ? 'var(--red)' : 'var(--text-3)',
            fontSize: 11,
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '3px 2px',
          }}
          onClick={() => pick('wrong')}
          disabled={busy}
        >
          Report wrong answer
        </button>
        {thanks && <span style={{ color: 'var(--green)' }}>{THANKS}</span>}
        {error && <span style={{ color: 'var(--red)' }}>{error}</span>}
      </div>

      {commentFor && (
        <form
          onSubmit={e => {
            e.preventDefault();
            submit(commentFor, comment.trim());
          }}
          style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <input
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={
              commentFor === 'wrong' ? "What's wrong? (optional)" : 'What was missing? (optional)'
            }
            maxLength={500}
            aria-label="Feedback comment"
            style={{
              flex: '1 1 200px',
              background: 'var(--surface-1, #ffffff)',
              border: '1px solid var(--border, #e3e7f0)',
              borderRadius: 8,
              padding: '6px 10px',
              fontSize: 12,
              fontFamily: 'var(--sans)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            className="btn btn-ghost"
            disabled={busy}
            style={{ padding: '6px 14px', fontSize: 11.5 }}
          >
            Send
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setCommentFor(null);
              setComment('');
            }}
            style={{ padding: '6px 10px', fontSize: 11.5 }}
          >
            Cancel
          </button>
        </form>
      )}
    </div>
  );
}
